/**
 * Approval requests (docs/teammate-human-approval-request-proposal.md in the SVN repo): an AI
 * prepares a human-actor activity and a person approves it here. The person's routes,
 * api/approval-requests, answer only a signed-in person (never an API token) and act in the
 * shared client's wsId workspace. A row is { requestId, status, module, moduleId, entryId,
 * entryTitle, activity, activityId, workspaceId, fromState, nextStates, recipients,
 * requestedBy: { username, displayName, agent }, summary, expiresAt, createdAt, decidedBy,
 * decidedAt, rejectReason, staleReason }; the view adds values (fieldName-keyed), providedFields,
 * fields and canDecide. Errors are { error, message }.
 */
import { commonStore } from "../store/commonStore";
import { NATIVE_ENGINE_VERSION } from "../../business/nativeFormula";

function client() {
  return commonStore.state.client;
}

/** The requests waiting for the signed-in person ("pending" also lists a failed approve). */
export function listApprovalRequests(status) {
  return client().get("/api/approval-requests", { params: { status: status || "pending" } });
}

export function getApprovalRequest(id) {
  return client().get(`/api/approval-requests/${encodeURIComponent(id)}`);
}

/** Runs the activity as the signed-in person, with exactly the values the request shows. */
export function approveApprovalRequest(id) {
  return client().post(`/api/approval-requests/${encodeURIComponent(id)}/approve`, {});
}

/** The reason is optional (HA-R17); it goes back to the agent that asked. */
export function rejectApprovalRequest(id, reason) {
  const trimmed = (reason || "").trim();
  return client().post(`/api/approval-requests/${encodeURIComponent(id)}/reject`, trimmed ? { reason: trimmed } : {});
}

/**
 * The activity's own form for the entry, as the detail view would open it - layout, field types,
 * stored values - read from the legacy route directly, so the shared classification store that
 * Quick View and the detail view use is never touched.
 */
export function getActivityForm({ moduleId, entryId, activityId }) {
  return client().post("/api/activity/form", {
    moduleId,
    activityId,
    entryId,
    locusId: entryId,
    query: null,
    nativeEngineVersion: NATIVE_ENGINE_VERSION,
  });
}

/** The request a notification is about, or null: an approval push is a content notification
 * whose source names the request. */
export function approvalRequestIdOf(notification) {
  const source = notification && notification.source;
  if (!source) return null;
  const kind = String(source.kind || "").toLowerCase();
  return kind === "approval" && source.approvalRequestId ? source.approvalRequestId : null;
}

/** The form's stored values with the request's values laid over them: what the approver sees
 * is what Approve submits. */
export function overlayValues(defaults, values) {
  return Object.assign({}, defaults || {}, values || {});
}

/** "Expires in 2d 4h", counted from `now`; null once past. */
export function expiresInText(expiresAt, now) {
  const ms = new Date(expiresAt).getTime() - (now || Date.now());
  if (!(ms > 0)) return null;
  const minutes = Math.floor(ms / 60000);
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  if (days > 0) return `Expires in ${days}d${hours ? ` ${hours}h` : ""}`;
  if (hours > 0) return `Expires in ${hours}h`;
  return `Expires in ${Math.max(1, minutes)}m`;
}

/** A state of the module, by id (the form names state ids) or by name (the request names states). */
export function findState(states, idOrName) {
  if (!idOrName || !Array.isArray(states)) return null;
  return states.find((s) => s.id == idOrName) || states.find((s) => s.name === idOrName) || null;
}

export const OPEN_STATUSES = ["pending", "failed"];

export function isOpen(request) {
  return !!request && OPEN_STATUSES.includes(request.status);
}
